import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Grid,
    MenuItem,
    Paper,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Draggable from "react-draggable";
import { useRef } from "react";
import { TaskService } from "../../service/taskService";
import { toast } from "react-toastify";
import { Colors } from "../../styles/theme";
import { isLocalDev } from "../../utils/environment";
import { useQuery } from "react-query";
import { EmployeeService } from "../../service/employeeService";
import SimpleDatePicker from "../ui/simpleDatePicker";
import { useLocation } from "../../hooks/useLocation";

const PaperComponent = (props) => {
    const nodeRef = useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#add-task-dialog-title">
            <Paper ref={nodeRef} {...props} />
        </Draggable>
    );
};

const validationSchema = Yup.object().shape({
    title: Yup.string().required("Task title is required").max(255),
    description: Yup.string().max(2000),
    priority: Yup.string().oneOf(["low", "normal", "high"]),
    dueDate: Yup.date().nullable(),
    assignedToUserId: Yup.number().required("Please assign task to a user"),
});

export default function AddTaskDialog({
    open,
    onClose,
    entityType,
    entityId,
    onTaskCreated,
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { selectedLocation } = useLocation();

    // Fetch employees for dropdown
    const { data: employeesData } = useQuery(
        ["employees"],
        () => EmployeeService.getAll(),
        {
            enabled: open,
            staleTime: 5 * 60 * 1000,
        }
    );

    const employees = employeesData?.data || [];

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            priority: "normal",
            dueDate: "",
            assignedToUserId: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                const taskData = {
                    taskableType: entityType,
                    taskableId: entityId,
                    title: values.title,
                    description: values.description,
                    priority: values.priority,
                    dueDate: values.dueDate || null,
                    assignedToUserId: parseInt(values.assignedToUserId),
                    locationId: selectedLocation?.id || null,
                };

                const response = await TaskService.create(taskData);
                const { code, message } = { ...response };

                if (code === 0) {
                    toast.success(message || "Task created successfully");
                    formik.resetForm();
                    onClose();
                    if (onTaskCreated) {
                        onTaskCreated();
                    }
                } else {
                    toast.warn(message || "Unable to create task");
                }
            } catch (error) {
                if (isLocalDev) console.log(error);
                toast.error("Something went wrong");
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
        >
            <DialogTitle
                id="add-task-dialog-title"
                sx={{
                    background: Colors.primary,
                    color: Colors.white,
                    cursor: "move",
                }}
            >
                Add to Task List
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Box component="form" sx={{mt: 2}}>
                    <Grid container spacing={2}>
                        {/* Location Display */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={selectedLocation?.name || "No location selected"}
                                size="small"
                                disabled
                                helperText="Task will be assigned to the current location"
                            />
                        </Grid>

                        {/* Title */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Task Title"
                                name="title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.title &&
                                    Boolean(formik.errors.title)
                                }
                                helperText={
                                    formik.touched.title && formik.errors.title
                                }
                                placeholder="e.g., Review Invoice #12345"
                                size="small"
                            />
                        </Grid>

                        {/* Assigned To */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Assign To"
                                name="assignedToUserId"
                                value={formik.values.assignedToUserId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.assignedToUserId &&
                                    Boolean(formik.errors.assignedToUserId)
                                }
                                helperText={
                                    formik.touched.assignedToUserId &&
                                    formik.errors.assignedToUserId
                                }
                                size="small"
                            >
                                {employees.map((emp) => (
                                    <MenuItem key={emp.id} value={emp.id}>
                                        {emp.firstName} {emp.lastName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Priority */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Priority"
                                name="priority"
                                value={formik.values.priority}
                                onChange={formik.handleChange}
                                size="small"
                            >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Due Date */}
                        <Grid item xs={12} sm={6}>
                            <SimpleDatePicker
                                label="Due Date (Optional)"
                                value={formik.values.dueDate}
                                onChange={(val) => formik.setFieldValue("dueDate", val || "")}
                                size="small"
                                error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
                                helperText={formik.touched.dueDate && formik.errors.dueDate ? formik.errors.dueDate : null}
                            />
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notes (Optional)"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.description &&
                                    Boolean(formik.errors.description)
                                }
                                helperText={
                                    formik.touched.description &&
                                    formik.errors.description
                                }
                                placeholder="Add any context or notes"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={formik.handleSubmit}
                    variant="contained"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Creating..." : "Add Task"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
